import { get, create } from "./apiService";

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface InvestmentProduct {
  _id: string;
  name: string;
  type: string;
  status: string;
  [key: string]: unknown;
}

export interface InvestmentApplication {
  _id: string;
  productId: string;
  amount: number;
  status: string;
  termMonths?: number;
  autoRenew?: boolean;
  [key: string]: unknown;
}

export interface CreateApplicationPayload {
  productId: string;
  accountId?: string;
  amount?: number;
  investmentAmount?: number;
  experience?: "beginner" | "intermediate" | "advanced";
  riskTolerance?: "low" | "moderate" | "high";
  primaryGoal?: "preserve_capital" | "generate_income" | "achieve_growth";
  investmentPeriod?: "less_than_2y" | "2_to_5y" | "more_than_5y";
  concerns?: "losing_money" | "low_returns" | "volatility";
  isInvestmentAgreed?: boolean;
  isTermsAccepted?: boolean;
  isFundsSourceAgreed?: boolean;
  source?: string;
}

export interface WithdrawApplicationResponse {
  message: string;
  [key: string]: unknown;
}

export interface InvestmentQuoteResponse {
  amount: number;
  formattedAmount?: string;
  totalAmount: number;
  formattedTotalAmount?: string;
  depositFee?: number;
  formattedDepositFee?: string;
  withdrawalFee?: number;
  formattedWithdrawalFee?: string;
  amountReceived?: number;
  netAmount?: number;
  [key: string]: unknown;
}

export interface PortfolioDetailResponse {
  _id: string;
  status?: string;
  capital?: number;
  profit?: number;
  balance?: number;
  availableAmount?: number;
  productId?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface PortfolioSummary {
  investments?: InvestmentApplication[];
  totalInvested: number;
  totalReturns: number;
  totalValue?: number;
  portfolioEstimate?: number;
  allocations: InvestmentApplication[];
  [key: string]: unknown;
}

export interface ProductTerms {
  [key: string]: unknown;
}

interface DecimalValue {
  $numberDecimal?: string;
}

export interface TransactionHistoryResponse {
  transactions: unknown[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

export interface ApplicationListResponse {
  applications: InvestmentApplication[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function toNumber(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (value && typeof value === "object" && "$numberDecimal" in value) {
    const decimalValue = (value as DecimalValue).$numberDecimal;
    const parsed = decimalValue ? parseFloat(decimalValue) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

function normalizeApplicationsResponse(
  payload: unknown
): ApplicationListResponse {
  if (Array.isArray(payload)) {
    return {
      applications: payload as InvestmentApplication[],
      total: payload.length,
      page: 1,
      limit: payload.length,
    };
  }

  if (isRecord(payload)) {
    const applications = Array.isArray(payload.applications)
      ? (payload.applications as InvestmentApplication[])
      : [];

    return {
      applications,
      total:
        typeof payload.total === "number" ? payload.total : applications.length,
      page: typeof payload.page === "number" ? payload.page : 1,
      limit:
        typeof payload.limit === "number"
          ? payload.limit
          : applications.length || 20,
    };
  }

  return {
    applications: [],
    total: 0,
    page: 1,
    limit: 20,
  };
}

function normalizePortfolioSummary(payload: unknown): PortfolioSummary {
  const summary = isRecord(payload) ? payload : {};
  const investments = Array.isArray(summary.investments)
    ? (summary.investments as InvestmentApplication[])
    : [];
  const totalValue =
    typeof summary.totalValue === "number" ? summary.totalValue : 0;

  return {
    ...summary,
    investments,
    allocations: investments,
    totalInvested:
      typeof summary.totalInvested === "number" ? summary.totalInvested : 0,
    totalReturns:
      typeof summary.totalReturns === "number" ? summary.totalReturns : 0,
    totalValue,
    portfolioEstimate: totalValue,
  };
}

export interface BrokerCommission {
  _id: string;
  [key: string]: unknown;
}

export interface BrokerStats {
  totalCommissions: number;
  activeReferrals: number;
  aum: number;
  [key: string]: unknown;
}

// ─── Products ───────────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/investments/products
export const getInvestProducts = async (
  type?: string,
  status?: string
): Promise<InvestmentProduct[]> => {
  const params: Record<string, unknown> = {};
  if (type) params.type = type;
  if (status) params.status = status;
  return get<InvestmentProduct[]>("/api/v2/investments/products", params);
};

// V2 ENDPOINT: GET /api/v2/investments/products/:id
export const getInvestProduct = async (
  id: string
): Promise<InvestmentProduct> => {
  return get<InvestmentProduct>(`/api/v2/investments/products/${id}`);
};

// V2 ENDPOINT: GET /api/v2/investments/products/:id/terms
export const getProductTerms = async (id: string): Promise<ProductTerms> => {
  return get<ProductTerms>(`/api/v2/investments/products/${id}/terms`);
};

// ─── Applications ───────────────────────────────────────────────────────────

// V2 ENDPOINT: POST /api/v2/investments/applications
export const createInvestmentApplication = async (
  data: CreateApplicationPayload
): Promise<InvestmentApplication> => {
  const normalizedPayload = {
    ...data,
    investmentAmount: toNumber(data.investmentAmount ?? data.amount),
  };

  return create<InvestmentApplication>(
    "/api/v2/investments/applications",
    normalizedPayload
  );
};

// V2 ENDPOINT: GET /api/v2/investments/applications
export const getApplications = async (
  page = 1,
  limit = 20,
  status?: string
): Promise<ApplicationListResponse> => {
  const params: Record<string, unknown> = { page, limit };
  if (status) params.status = status;
  const response = await get<InvestmentApplication[] | ApplicationListResponse>(
    "/api/v2/investments/applications",
    params
  );
  return normalizeApplicationsResponse(response);
};

// V2 ENDPOINT: GET /api/v2/investments/applications/:id
export const getApplication = async (
  applicationId: string
): Promise<InvestmentApplication> => {
  return get<InvestmentApplication>(
    `/api/v2/investments/applications/${applicationId}`
  );
};

// V2 ENDPOINT: POST /api/v2/investments/applications/:id/withdraw
export const withdrawApplication = async (
  applicationId: string
): Promise<WithdrawApplicationResponse> => {
  return create<WithdrawApplicationResponse>(
    `/api/v2/investments/applications/${applicationId}/withdraw`
  );
};

// ─── Portfolio ──────────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/investments/portfolio
export const getInvestPortfolio = async (): Promise<PortfolioSummary> => {
  const response = await get<PortfolioSummary>("/api/v2/investments/portfolio");
  return normalizePortfolioSummary(response);
};

// V2 ENDPOINT: GET /api/v2/investments/portfolio/:applicationId/transactions
export const getTransactionHistory = async (
  applicationId: string,
  page = 1,
  limit = 20
): Promise<TransactionHistoryResponse> => {
  const params: Record<string, unknown> = { page, limit };
  return get<TransactionHistoryResponse>(
    `/api/v2/investments/portfolio/${applicationId}/transactions`,
    params
  );
};

// ─── Broker ─────────────────────────────────────────────────────────────────

// V2 ENDPOINT: GET /api/v2/investments/broker/commissions
export const getBrokerCommissions = async (
  page = 1,
  limit = 20
): Promise<{ commissions: BrokerCommission[]; total: number }> => {
  return get("/api/v2/investments/broker/commissions", { page, limit });
};

// V2 ENDPOINT: GET /api/v2/investments/broker/stats
export const getBrokerStats = async (): Promise<BrokerStats> => {
  return get<BrokerStats>("/api/v2/investments/broker/stats");
};

// ─── Legacy aliases ─────────────────────────────────────────────────────────

/** @deprecated Use getApplications */
export const getPendingLedgers = async (): Promise<ApplicationListResponse> => {
  return getApplications(1, 100, "pending");
};

/** @deprecated Use getApplication */
export const getExistingApplication = getApplication;

/** @deprecated Use getTransactionHistory */
export const getProductTransactionHistory = async (
  applicationId: string
): Promise<TransactionHistoryResponse> => {
  return getTransactionHistory(applicationId);
};

/** @deprecated Use getInvestPortfolio */
export const getLedgerEntries = getInvestPortfolio;

/** @deprecated Stub - not yet implemented in V2 */
export const createDeposit = async (
  data: Record<string, unknown>
): Promise<PortfolioDetailResponse> => {
  const ledgerId = String(data.ledgerId ?? data.applicationId ?? "");
  const amount = toNumber(data.amount);

  if (!ledgerId) {
    throw new Error("Investment application id is required");
  }

  return create<PortfolioDetailResponse>(
    `/api/v2/investments/portfolio/${ledgerId}/deposit`,
    { amount, twoFactorToken: data.twoFactorToken }
  );
};

export const createDepositQuote = async (
  data: Record<string, unknown>
): Promise<InvestmentQuoteResponse> => {
  const ledgerId = String(data.ledgerId ?? data.applicationId ?? "");
  const amount = toNumber(data.amount);

  if (!ledgerId) {
    throw new Error("Investment application id is required");
  }

  return create<InvestmentQuoteResponse>(
    `/api/v2/investments/portfolio/${ledgerId}/deposit-quote`,
    { amount }
  );
};

export const createWithdraw = async (
  data: Record<string, unknown>,
  ledgerId?: string
): Promise<PortfolioDetailResponse> => {
  const resolvedLedgerId = String(
    ledgerId ?? data.ledgerId ?? data.applicationId ?? ""
  );
  const amount = toNumber(data.amount);

  if (!resolvedLedgerId) {
    throw new Error("Investment application id is required");
  }

  return create<PortfolioDetailResponse>(
    `/api/v2/investments/portfolio/${resolvedLedgerId}/withdraw`,
    { amount, twoFactorToken: data.twoFactorToken }
  );
};

export const createWithdrawQuote = async (
  data: Record<string, unknown>
): Promise<InvestmentQuoteResponse> => {
  const ledgerId = String(data.ledgerId ?? data.applicationId ?? "");
  const amount = toNumber(data.amount);

  if (!ledgerId) {
    throw new Error("Investment application id is required");
  }

  return create<InvestmentQuoteResponse>(
    `/api/v2/investments/portfolio/${ledgerId}/withdraw-quote`,
    { amount }
  );
};

export const getAvalableAmount = async (
  ledgerId: string
): Promise<{ availableAmount: number }> => {
  const entry = await get<PortfolioDetailResponse>(
    `/api/v2/investments/portfolio/${ledgerId}/balance`
  );
  const availableAmount = toNumber(entry.availableAmount);

  return { availableAmount };
};

/** @deprecated Stub - not yet implemented in V2 */
export const getInvestmentPayWith = async (
  ledgerId: string,
  assetName: string
): Promise<{ ledgerId: string; assetName: string }> => {
  return { ledgerId, assetName };
};

export const getSingleInvestment = async (
  ledgerId: string
): Promise<Record<string, unknown> | null> => {
  return get<Record<string, unknown>>(
    `/api/v2/investments/portfolio/${ledgerId}`
  );
};

/** @deprecated Stub - not yet implemented in V2 */
export const investPerformanceGraph = async (
  productId: string,
  principle: number,
  timeframe: string
): Promise<{ performanceData: Array<{ label: string; value: number }> }> => {
  const product = await getInvestProduct(productId);
  const roi = toNumber((product as Record<string, unknown>).roi);
  const duration = Math.max(
    1,
    Math.round(
      toNumber(
        (product as Record<string, unknown>).duration ||
          (product as Record<string, unknown>).termMonths
      ) || 12
    )
  );

  const totalMonths =
    timeframe === "weekly" ? 3 : timeframe === "daily" ? 1 : duration;
  const monthlyRate = roi > 0 ? roi / 100 / 12 : 0.01;

  const performanceData = Array.from({ length: totalMonths }, (_, index) => {
    const monthIndex = index + 1;
    const value = principle * (1 + monthlyRate * monthIndex);
    const date = new Date();
    date.setMonth(date.getMonth() + monthIndex);

    return {
      label: date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      }),
      value,
    };
  });

  return { performanceData };
};
